export const widgetEvents = {
  widgetReady: 'WIDGET_READY',
  widgetRemoved: 'WIDGET_REMOVED',
  updateConfig: 'UPDATE_CONFIG',
  validateForm: 'VALIDATE_FORM',
  validityStatus: 'VALIDITY_STATUS',
  validationResult: 'VALIDATION_RESULT',
  sendForm: 'SEND_FORM',
  resetForm: 'RESET_FORM',
  addLoader: 'ADD_LOADER',
  removeLoader: 'REMOVE_LOADER',
  changeLanguage: 'CHANGE_LANGUAGE',
  formSuccess: 'FORM_SUCCESS',
  formError: 'FORM_ERROR',
  enterKey: 'ENTER_KEY',
  heightChange: 'HEIGHT_CHANGE',
  viewChange: 'VIEW_CHANGE',
  showModal: 'SHOW_MODAL'
}

const listeners = []

const addLog = (message) => {
  console.log('WidgetConnection: ' + message)
}

export class WidgetConnection {
  constructor (config, debug) {
    this.iframeEl = null
    this.iframeOrigin = null
    if (config && typeof config === 'object') {
      this.updateConfig(config)
    }
    this.resetFlags()
    this.debug = !!debug
  }

  startConnection (iframeEl) {
    return new Promise((resolve, reject) => {
      if (this.iframeEl) {
        this.stopConnection()
      }
      if (!(iframeEl instanceof HTMLIFrameElement)) {
        reject(new Error('iframeEl should be an HTML iframe element'))
        return
      }
      this.iframeEl = iframeEl
      this.iframeOrigin = typeof URL !== 'undefined' ? new URL(iframeEl.src).origin : iframeEl.src.replace(/\.pl.+$/, '.pl')
      this.messageListener = this.messageListener.bind(this, resolve)
      window.addEventListener('message', this.messageListener)
      this.resetFlags()
      if (this.debug) {
        addLog('connection to widget started')
      }
    })
  }

  stopConnection () {
    window.removeEventListener('message', this.messageListener)
    this.iframeEl = null
    this.iframeOrigin = null
    if (this.debug) {
      addLog('connection to widget stopped')
    }
  }

  resetFlags () {
    this.ready = false
    this.invalid = true
    this.valid = false
    this.pristine = true
    this.dirty = false
    this.touched = false
    this.submitted = false
    if (this.debug) {
      addLog('widget status flags reset')
    }
  }

  on (eventName, callback) {
    listeners.push({ id: this.iframeId, eventName: eventName, callback: callback })
    if (this.debug) {
      addLog('listener to event ' + eventName + ' added')
    }
  }

  messageListener (resolve, event) {
    if (typeof event.data !== 'string') {
      return
    }
    if (event.origin !== this.iframeOrigin) {
      if (this.debug) {
        addLog('event origin mismatched: ' + event.origin)
      }
      return
    }
    let eventData
    try {
      eventData = JSON.parse(event.data)
    } catch (e) {
      if (this.debug) {
        addLog('cannot parse event data: ' + event.data)
      }
    }

    if (!eventData) {
      return
    }

    if (this.iframeId && eventData.id && this.iframeId !== eventData.id) {
      return
    }

    if (this.debug) {
      addLog('event passed from widget: ' + eventData.status)
    }

    switch (eventData.status) {
      case widgetEvents.widgetReady:
        if (eventData.id) {
          this.iframeId = eventData.id
        }
        if (eventData.height) {
          this.iframeEl.style.height = eventData.height + 'px'
        }
        this.ready = true
        this.sendMessage('WIDGET_V2', null)
        if (this.iframeUpdatePending) {
          this.updateConfig(this.iframeUpdatePending)
        }
        resolve()
        break

      case widgetEvents.enterKey:
        if (/firefox/i.test(navigator.userAgent)) {
          return
        }
        break

      case widgetEvents.heightChange:
        const newHeight = parseInt(eventData.iframeHeight || eventData.height || eventData.message, 10)
        if (isNaN(newHeight)) {
          return
        }
        this.iframeEl.style.height = newHeight + 'px'
        break

      case widgetEvents.validityStatus:
        if (!this.dirty) {
          this.dirty = true
          this.pristine = false
        }
        if (!this.touched) {
          this.touched = true
        }
        this.valid = eventData.valid
        this.invalid = !eventData.valid
        break

      case widgetEvents.validationResult:
        this.valid = eventData.valid
        this.invalid = !eventData.valid
        break
    }

    listeners.forEach(listenerData => {
      if ((listenerData.id && listenerData.id !== this.iframeId) || listenerData.eventName !== eventData.status) {
        return
      }
      listenerData.callback(eventData.message, eventData)
    })
  }

  sendMessage (eventName, message) {
    if (!this.iframeEl || !this.iframeEl.contentWindow || !eventName || typeof eventName !== 'string') {
      return
    }
    message = message || ''
    this.iframeEl.contentWindow.postMessage(JSON.stringify({ status: eventName, message: message }), this.iframeOrigin)
    if (this.debug) {
      addLog('event sent to widget: ' + eventName)
    }
  }

  updateConfig (message) {
    if (!this.ready) {
      this.iframeUpdatePending = message
      return
    }
    this.iframeUpdatePending = null
    this.sendMessage(widgetEvents.updateConfig, message)
  }

  changeLanguage (language) {
    if (typeof language !== 'string') {
      return
    }
    this.sendMessage(widgetEvents.changeLanguage, language.toLowerCase().slice(0, 2))
  }

  addLoader () {
    this.sendMessage(widgetEvents.addLoader, null)
  }

  removeLoader () {
    this.sendMessage(widgetEvents.removeLoader, null)
  }

  validateForm () {
    this.submitted = true
    this.sendMessage(widgetEvents.validateForm, null)
  }

  sendForm () {
    this.submitted = true
    this.sendMessage(widgetEvents.sendForm, null)
  }

  resetForm () {
    this.sendMessage(widgetEvents.resetForm, null)
  }
}

export default {
  widgetEvents,
  WidgetConnection
}
